import apiService, { refreshAxios } from "./apiService";
import { LoginRequest, LoginResponse, User, Company } from "../types";

/**
 * Business-outcome code returned by /api/auth/forgot-password when the account
 * exists but carries no email address, so no reset link can be sent.
 */
export const FORGOT_PASSWORD_NO_EMAIL = "EMAIL_NOT_CONFIGURED";

/** Error carrying the backend's machine-readable `code` alongside its message. */
const codedError = (message: string, code?: string, data?: any) => {
  const error: any = new Error(message);
  error.code = code;
  error.data = data;
  return error;
};

/**
 * A mobile number that carries a vendor account in more than one company —
 * the caller has to ask which before an OTP can be sent. `error.data.companies`
 * holds the choices.
 */
export const VENDOR_MULTIPLE_ACCOUNTS = "MULTIPLE_ACCOUNTS";

/** The OTP screen has to start over: the challenge is gone or spent. */
export const VENDOR_OTP_RESTART_CODES = [
  "OTP_EXPIRED",
  "OTP_ATTEMPTS_EXCEEDED",
  "VENDOR_INACTIVE",
];

/** An issued OTP challenge — what the verify step needs to redeem it. */
export interface VendorOtpChallenge {
  requestId: string;
  maskedMobile: string;
  name?: string;
  companyName?: string | null;
  expiresInSec: number;
  resendAfterSec: number;
  /** Only present when the backend runs with VENDOR_OTP_DEBUG on, for QA. */
  devOtp?: string;
}

class AuthService {
  /**
   * Login user
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response: any = await apiService.post(
        "/api/auth/login",
        credentials,
      );

      if (response.success && response.data) {
        // ✅ Tokens are now HttpOnly cookies (set by server)
        // ✅ No need to store in localStorage
        return response.data;
      }

      throw new Error(response.message || "Login failed");
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || "Login failed",
      );
    }
  }

  /**
   * Logout user
   */
  async logout(reason: string = "Manual"): Promise<void> {
    try {
      // ✅ Cookies cleared by server automatically
      await apiService.post("/api/auth/logout", { reason });
    } catch (error) {
      console.error("Logout error:", error);
    }
  }

  /**
   * Logout from all devices
   */
  async logoutAll(): Promise<void> {
    try {
      // ✅ Cookies cleared by server automatically
      await apiService.post("/api/auth/logout-all");
    } catch (error) {
      console.error("Logout all error:", error);
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken(): Promise<{ success: boolean } | null> {
    try {
      // ✅ Use refreshAxios (no 401 interceptor) to avoid refresh loop
      const response: any = await refreshAxios.post("/api/auth/refresh-token");

      if (response.data?.success) {
        // ✅ New tokens set as cookies by server
        return response.data;
      }

      return null;
    } catch (error) {
      // Network error or server rejection — don't force logout
      // The 401 interceptor on regular API calls handles real session expiry
      return null;
    }
  }

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      // ✅ Uses HttpOnly cookies automatically
      // _skipRefresh: true prevents the interceptor from attempting a token
      // refresh when this is called before login (no cookies yet).
      const response: any = await apiService.get("/api/auth/me", {
        _skipRefresh: true,
        timeout: 5000,
      });
      console.log("getCurrentUser response:", response);
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Generate a short-lived handoff token for cross-service redirect.
   * Must be called after login (session cookie is already set).
   */
  async generateHandoffToken(): Promise<string | null> {
    try {
      const response: any = await apiService.post("/api/auth/generate-handoff");
      if (response.success && response.data?.handoffToken) {
        return response.data.handoffToken;
      }
      return null;
    } catch (error) {
      console.error("Failed to generate handoff token:", error);
      return null;
    }
  }

  /**
   * Get all companies
   */
  async getCompanies(): Promise<Company[]> {
    try {
      const response: any = await apiService.get("/api/company");
      if (response.success && response.data) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error("Get companies error:", error);
      return [];
    }
  }

  /**
   * Validate an Azure AD token received via callback redirect.
   * Calls the uib backend's /auth/azure/validate endpoint.
   */
  async validateAzureToken(token: string): Promise<any | null> {
    const azureApiUrl =
      process.env.REACT_APP_AZURE_API_URL ||
      process.env.REACT_APP_API_URL ||
      "http://localhost:9005";
    try {
      const response = await fetch(`${azureApiUrl}/auth/azure/validate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.status === "Ok" && data.data?.valid) {
        return data.data.user;
      }
      return null;
    } catch (error) {
      console.error("Azure token validation error:", error);
      return null;
    }
  }

  /**
   * Request a password-reset mail.
   * The backend mails a link pointing at `<identity-frontend>/reset-password?token=...`.
   * `_skipRefresh` keeps the 401 interceptor from bouncing this public call to /login.
   */
  async forgotPassword(payload: {
    username: string;
    company_id: number;
  }): Promise<{ success: true; message?: string }> {
    const fallback = "Unable to send the reset link. Please try again.";
    let response: any;

    try {
      response = await apiService.post("/api/auth/forgot-password", payload, {
        _skipRefresh: true,
        // The request stays open across the SMTP handshake and send, which
        // measured ~14s against the Gmail relay — past the 15s default, so the
        // user saw a timeout error while the mail was actually going out.
        timeout: 45000,
      });
    } catch (error: any) {
      const data = error.response?.data;
      throw codedError(data?.message || error.message || fallback, data?.code);
    }

    // A business outcome can arrive as 200 + success:false (e.g. no email on
    // record), so resolving must depend on the flag, not just the HTTP status.
    if (!response?.success) {
      throw codedError(response?.message || fallback, response?.code);
    }

    return { success: true, message: response?.message };
  }

  /**
   * Set a new password using the token from the reset mail.
   */
  async resetPassword(payload: {
    token: string;
    password: string;
  }): Promise<{ success: boolean; message?: string }> {
    try {
      const response: any = await apiService.post(
        "/api/auth/reset-password",
        payload,
        { _skipRefresh: true },
      );
      if (!response?.success) {
        throw new Error(response?.message || "Unable to reset the password");
      }
      return { success: true, message: response?.message };
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "This reset link is invalid or has expired.",
      );
    }
  }

  /**
   * Vendor sign-in step 1 — ask for an OTP on a registered mobile number.
   *
   * `_skipRefresh` keeps the 401 interceptor out of a public call: a rejected
   * OTP must surface on the screen, not bounce the user to the login page.
   *
   * Throws with `code` VENDOR_MULTIPLE_ACCOUNTS when the number is registered
   * with several companies; `error.data.companies` is the list to choose from.
   */
  async vendorSendOtp(payload: {
    mobile: string;
    company_id?: number | null;
  }): Promise<VendorOtpChallenge> {
    return this.vendorOtpRequest("/api/auth/vendor/send-otp", {
      mobile: payload.mobile,
      ...(payload.company_id ? { company_id: payload.company_id } : {}),
    });
  }

  /** Vendor sign-in step 1a — a fresh code on the same challenge. */
  async vendorResendOtp(requestId: string): Promise<VendorOtpChallenge> {
    return this.vendorOtpRequest("/api/auth/vendor/resend-otp", { requestId });
  }

  /** Shared plumbing for send/resend — both answer with the same challenge shape. */
  private async vendorOtpRequest(
    url: string,
    body: Record<string, any>,
  ): Promise<VendorOtpChallenge> {
    const fallback = "Unable to send the OTP. Please try again.";
    let response: any;

    try {
      response = await apiService.post(url, body, { _skipRefresh: true });
    } catch (error: any) {
      const data = error.response?.data;
      throw codedError(
        data?.message || error.message || fallback,
        data?.code,
        data?.data,
      );
    }

    // A business outcome can arrive as 200 + success:false (several companies
    // on one number), so resolving must depend on the flag, not the status.
    if (!response?.success) {
      throw codedError(response?.message || fallback, response?.code, response?.data);
    }

    return response.data as VendorOtpChallenge;
  }

  /**
   * Vendor sign-in step 2 — redeem the OTP.
   * On success the session cookies are already set by the server.
   */
  async vendorVerifyOtp(payload: {
    requestId: string;
    otp: string;
  }): Promise<{ user: User }> {
    const fallback = "Could not verify the OTP. Please try again.";
    let response: any;

    try {
      response = await apiService.post("/api/auth/vendor/verify-otp", payload, {
        _skipRefresh: true,
      });
    } catch (error: any) {
      const data = error.response?.data;
      throw codedError(
        data?.message || error.message || fallback,
        data?.code,
        data?.data,
      );
    }

    if (!response?.success) {
      throw codedError(response?.message || fallback, response?.code, response?.data);
    }

    return response.data;
  }

  /**
   * Escalation matrix behind the "Contact IT support" popup.
   * Public call made before a session exists, so `_skipRefresh` keeps the 401
   * interceptor from bouncing it to /login.
   */
  async getEscalationMatrix(): Promise<any[]> {
    const response: any = await apiService.get("/api/support/escalation-matrix", {
      _skipRefresh: true,
    });
    if (response.success && Array.isArray(response.data)) {
      return response.data;
    }
    throw new Error(response?.message || "Unable to load the escalation matrix");
  }

  /**
   * Get user companies by E-Code/email.
   * Returns company_id (auto-selected if single) and companies array.
   *
   * `null` means the lookup itself failed (network/server) — nothing can be
   * concluded about the key. A key that matches no active user is a definite
   * answer and comes back as an empty `companies` list, so the caller can tell
   * "not on record" apart from "could not check".
   */
  async getUserCompany(
    key: string,
  ): Promise<{ company_id: number | null; companies: any[] } | null> {
    try {
      const response: any = await apiService.post(
        "/api/company/user-company",
        { key },
        { _skipRefresh: true },
      );
      if (!response?.success) {
        return null;
      }
      // No match is returned as success + data:null ("Company not found").
      return response.data ?? { company_id: null, companies: [] };
    } catch (error) {
      return null;
    }
  }
}

export default new AuthService();
